import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Schemas } from '@npc/api-client'

import { api } from '../../app/api'
import { call } from '../../app/errors'

export type ModelConnection = Schemas['ModelConnection']
export type ModelProfile = Schemas['ModelProfile']
export type ModelProvider = Schemas['ModelLibraryProvider']
export type ModelProtocol = Schemas['ModelProtocol']
export type ModelConfigProtocol = Schemas['ModelConfigProtocol']
export type DiscoveredModel = Schemas['DiscoveredModelList']['models'][number]

export function normalizeDiscoveredModels(models: unknown): DiscoveredModel[] {
  if (!Array.isArray(models)) return []
  return models.filter(
    (model): model is DiscoveredModel =>
      typeof model === 'object' &&
      model !== null &&
      typeof (model as { model_id?: unknown }).model_id === 'string' &&
      (model as { model_id: string }).model_id.trim().length > 0,
  )
}

const key = {
  providers: ['model-library', 'providers'] as const,
  connections: ['model-library', 'connections'] as const,
  profiles: (projectId?: string) => ['model-library', 'profiles', projectId ?? null] as const,
}

export function useModelProviders() {
  return useQuery({
    queryKey: key.providers,
    queryFn: () => call('/model-library/providers', () => api.GET('/model-library/providers')),
  })
}

export function useModelConnections() {
  return useQuery({
    queryKey: key.connections,
    queryFn: () => call('/model-library/connections', () => api.GET('/model-library/connections')),
  })
}

export function useModelProfiles(projectId?: string) {
  return useQuery({
    queryKey: key.profiles(projectId),
    queryFn: () =>
      call('/model-library/profiles', () =>
        api.GET('/model-library/profiles', {
          params: {
            query: projectId
              ? { project_id: projectId, include_disabled: true }
              : { include_disabled: true },
          },
        }),
      ),
  })
}

function useInvalidateLibrary() {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: key.connections })
    void client.invalidateQueries({ queryKey: ['model-library', 'profiles'] })
  }
}

export function useCreateModelConnection() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (input: Schemas['ModelConnectionCreate']) =>
      call('/model-library/connections', () =>
        api.POST('/model-library/connections', { body: input }),
      ),
    onSuccess: invalidate,
  })
}

/**
 * 「贴一段代码，我帮你填」。
 *
 * 它**不建任何东西**——回来的是一份草稿，填进表单等人核。所以这里
 * 刻意不 invalidate 任何查询：没有东西变过。
 */
export function useDraftModelConnection() {
  return useMutation({
    mutationFn: (text: string) =>
      call('/model-library/connections/draft', () =>
        api.POST('/model-library/connections/draft', { body: { text } }),
      ),
  })
}

export function useCreateModelProfile() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (input: Schemas['ModelProfileCreate']) =>
      call('/model-library/profiles', () => api.POST('/model-library/profiles', { body: input })),
    onSuccess: invalidate,
  })
}

export function useUpdateModelConnection() {
  const invalidate = useInvalidateLibrary()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { connectionId: string; body: Schemas['ModelConnectionUpdate'] }) =>
      call('/model-library/connections/{connectionId}', () =>
        api.PATCH('/model-library/connections/{connectionId}', {
          params: { path: { connectionId: input.connectionId } },
          body: input.body,
        }),
      ),
    onSuccess: (result) => {
      client.setQueryData<Schemas['ModelConnectionList']>(key.connections, (current) =>
        current
          ? {
              connections: current.connections.map((connection) =>
                connection.id === result.connection.id ? result.connection : connection,
              ),
            }
          : current,
      )
      invalidate()
    },
  })
}

export function useUpdateModelProfile() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (input: { profileId: string; body: Schemas['ModelProfileUpdate'] }) =>
      call('/model-library/profiles/{profileId}', () =>
        api.PATCH('/model-library/profiles/{profileId}', {
          params: { path: { profileId: input.profileId } },
          body: input.body,
        }),
      ),
    onSuccess: invalidate,
  })
}

export function useProbeModelConnection() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (connectionId: string) =>
      call('/model-library/connections/{connectionId}/probe', () =>
        api.POST('/model-library/connections/{connectionId}/probe', {
          params: { path: { connectionId } },
        }),
      ),
    onSettled: invalidate,
  })
}

export function useDiscoverModels() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (connectionId: string) =>
      call('/model-library/connections/{connectionId}/discover', () =>
        api.POST('/model-library/connections/{connectionId}/discover', {
          params: { path: { connectionId } },
        }),
      ),
    onSettled: invalidate,
  })
}

export function useProbeModelProfile() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (profileId: string) =>
      call('/model-library/profiles/{profileId}/probe', () =>
        api.POST('/model-library/profiles/{profileId}/probe', { params: { path: { profileId } } }),
      ),
    onSettled: invalidate,
  })
}

export function useModelProfileImpact(profileId: string | null) {
  return useQuery({
    queryKey: ['model-library', 'impact', profileId],
    enabled: profileId !== null,
    // This answer guards a destructive action. An Agent or system binding can
    // be created after the previous dialog closed, so cached zero-reference
    // data must never authorize a later delete.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () =>
      call('/model-library/profiles/{profileId}/impact', () =>
        api.GET('/model-library/profiles/{profileId}/impact', {
          params: { path: { profileId: profileId as string } },
        }),
      ),
  })
}

export function useDeleteModelProfile() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (profileId: string) =>
      call('/model-library/profiles/{profileId}', () =>
        api.DELETE('/model-library/profiles/{profileId}', { params: { path: { profileId } } }),
      ),
    onSuccess: invalidate,
  })
}

export function useRetireModelProfile() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (input: { profileId: string; body: Schemas['ModelProfileRetireRequest'] }) =>
      call('/model-library/profiles/{profileId}/retire', () =>
        api.POST('/model-library/profiles/{profileId}/retire', {
          params: { path: { profileId: input.profileId } },
          body: input.body,
        }),
      ),
    onSuccess: invalidate,
  })
}

export function useDeleteModelConnection() {
  const invalidate = useInvalidateLibrary()
  return useMutation({
    mutationFn: (connectionId: string) =>
      call('/model-library/connections/{connectionId}', () =>
        api.DELETE('/model-library/connections/{connectionId}', {
          params: { path: { connectionId } },
        }),
      ),
    onSuccess: invalidate,
  })
}
